import React, { useRef } from "react";
import styled from "styled-components";
import {
	matchPath,
	Route,
	Switch,
	useLocation,
	useRouteMatch,
} from "react-router-dom";
import Routes, { DivisionRouteParams } from "@service/navigation/routes";
import {
	Button,
	LoadingIndicator,
	PageContainer,
	PageHeadContainer,
	PageHeading,
	Tab,
	ScrollToTop,
	ScrollUpButton,
	MainBreadcrumbs,
	QueryStateIndicators,
	ErrorImage,
	Icon,
} from "@components";

import UsersList, { UsersListRef } from "./components/UsersList";
import { RelativeContainer, Subheading } from "@components/PageStyle";
import ReportsPage from "../reports/ReportsPage";
import DivisionSettingsPage from "./components/DivisionSettingsPage";
import VouchersPage from "../vouchers/DefaultFilterableVoucherList";
import AddUserPage from "../user/components/AddUserPage";
import ImportCSVPage from "./components/ImportCSVPage/ImportCSVPage";
import { useCustomerAndDivision } from "@hooks";
import AccountingMonthsPage from "../accountingMonths/AccountingMonthsPage";
import DivisionPageContext from "./DivisionPageContext";
import { envConfig } from "@service/config";
import { StatusSpan, StatusToggle } from "@components/StatusToggle";
import { colors } from "@theme/theming";
import { usePageTitle } from "@hooks";
import { Division } from "@models";

type DivisionTabPayload = {
	division: Division;
	usersListRef: React.RefObject<UsersListRef>;
};

const DIVISION_SUBROUTES = [
	{
		route: Routes.Users,
		title: "Benutzer",
		exact: true as const,
		renderContent: ({ usersListRef }: DivisionTabPayload) => (
			<UsersList ref={usersListRef} />
		),
	},
	{
		route: Routes.DivisionVouchers,
		title: "Belege",
		renderContent: ({ division }: DivisionTabPayload) => (
			<VouchersPage requiredFilters={{ divisionId: [division.divisionId] }} />
		),
	},
	{
		route: Routes.Reports,
		title: "Reports",
		renderContent: () => <ReportsPage />,
	},
	{
		route: Routes.AccountingMonths,
		title: "Monatsabschlüsse",
		renderContent: () => <AccountingMonthsPage />,
	},
	{
		route: Routes.Settings,
		title: "Einstellungen",
		renderContent: () => <DivisionSettingsPage />,
	},
	{
		route: Routes.CsvImports,
		title: "Import",
		exact: true as const,
		renderContent: ({ division }: DivisionTabPayload) => (
			<ImportCSVPage division={division} />
		),
	},
	{
		route: Routes.AddUser,
		title: "Neuer Benutzer",
		exact: true as const,
		isTab: false as const,
		renderContent: () => <AddUserPage />,
	},
];

export default function DivisionsPage() {
	const routeParams = useRouteMatch<DivisionRouteParams>().params;

	const divisionAndCustomerQuery = useCustomerAndDivision();
	const cachedLocation = useLocation();
	const usersListRef = useRef<UsersListRef>(null);

	const activeSubroute = DIVISION_SUBROUTES.find(({ route }) =>
		matchPath(cachedLocation.pathname, { path: route.route })
	);

	const data = divisionAndCustomerQuery.data;
	usePageTitle(
		data
			? [activeSubroute?.title, data.division.name, data.customer.name]
					.filter(Boolean)
					.join(" · ")
			: undefined
	);

	const onStatusChanged = () => {
		usersListRef.current?.triggerRefresh();
		void divisionAndCustomerQuery.refetch();
	};

	return (
		<>
			<ScrollToTop />
			<ScrollUpButton />
			<PageContainer>
				{divisionAndCustomerQuery.state === "error" ? (
					<ErrorImage />
				) : (
					<>
						{divisionAndCustomerQuery.state !== "success" && (
							<LoadingIndicator />
						)}
						{divisionAndCustomerQuery.data && (
							<>
								<MainBreadcrumbs
									customer={divisionAndCustomerQuery.data.customer}
									division={divisionAndCustomerQuery.data.division}
								/>

								<RelativeContainer>
									<PageHeadContainer>
										<BasicInfoContainer>
											<PageHeading data-testid="userspage-division-name">
												{divisionAndCustomerQuery.data.division.name}
												<PortalLink
													customerId={
														divisionAndCustomerQuery.data.customer.customerId
													}
													divisionId={
														divisionAndCustomerQuery.data.division.divisionId
													}
												/>
											</PageHeading>
											<Subheading data-testid="userspage-customer-name">
												{divisionAndCustomerQuery.data.customer.name}
											</Subheading>
											<ExtraInfoContainer>
												<StatusSpan
													status={divisionAndCustomerQuery.data.division.status}
												/>
												{divisionAndCustomerQuery.data.division.comment && (
													<CommentContainer>
														{`“ ${divisionAndCustomerQuery.data.division.comment} ”`}
													</CommentContainer>
												)}
											</ExtraInfoContainer>
										</BasicInfoContainer>
										<StatusToggle
											target="division"
											targetId={
												divisionAndCustomerQuery.data.division.divisionId
											}
											targetName={divisionAndCustomerQuery.data.division.name}
											curStatus={divisionAndCustomerQuery.data.division.status}
											onSuccess={onStatusChanged}
										/>
									</PageHeadContainer>
									<Tab.Container
										css={`
											margin-bottom: 1rem;
										`}
									>
										{DIVISION_SUBROUTES.filter((s) => s.isTab !== false).map(
											(subroute) => (
												<Tab.Item
													key={subroute.route.route}
													active={activeSubroute === subroute}
													to={subroute.route.makePath(routeParams)}
												>
													{subroute.title}
												</Tab.Item>
											)
										)}
									</Tab.Container>

									<DivisionPageContext.Provider
										value={{
											...divisionAndCustomerQuery.data,
											updateDivision: divisionAndCustomerQuery.setDivision,
										}}
									>
										<Switch location={cachedLocation}>
											{DIVISION_SUBROUTES.map((subroute) => (
												<Route
													key={subroute.route.route}
													exact={subroute.exact}
													path={subroute.route.route}
												>
													{subroute.renderContent({
														division: divisionAndCustomerQuery.data.division,
														usersListRef,
													})}
												</Route>
											))}
										</Switch>
									</DivisionPageContext.Provider>
								</RelativeContainer>
							</>
						)}
					</>
				)}
				<QueryStateIndicators queryState={divisionAndCustomerQuery} />
			</PageContainer>
		</>
	);
}

const ExtraInfoContainer = styled.div`
	display: flex;
	gap: 1ch;
	align-items: center;
`;

const CommentContainer = styled.span`
	border: 1px solid ${colors.gray.g400};
	color: ${colors.gray.g400};
	font-style: italic;
	padding: 3px 5px;
	border-radius: 5px;
`;

export const BasicInfoContainer = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
`;
export const ButtonMiddle = styled(Button)`
	font-size: 0.7rem;
	min-width: 150px;
`;

const PortalLinkA = styled.a`
	font-size: 18px;
	margin-left: 1em;
`;

function PortalLink(props: { customerId: string; divisionId: string }) {
	return (
		<PortalLinkA
			href={`${envConfig.portalBaseUrl}customers/${props.customerId}/divisions/${props.divisionId}/home?as_internal=true`}
			target="_blank"
		>
			zum Arbeitgeberportal <Icon.PortalLinkOut />
		</PortalLinkA>
	);
}
